/**
 * Alert-state guard shooting. Runs per-tick after awareness has updated
 * guard.fovTiles / guard.state and before movement, so a firing guard
 * can hold its position while shooters that can't reach the player still
 * move.
 *
 * Spawns bullets rather than resolving hitscan directly — the bullet's
 * plan carries the guard's hit/miss + limb decision, and damage is
 * applied when the bullet impacts the player tile.
 *
 * Headless — no PixiJS.
 */

import { guardHasLosToPlayer } from './awareness';
import { canOperateWeapon, pickLimb } from './body';
import { spawnBullet, type Bullet } from './bullet';
import { attemptRangedShot, hitChance } from './combat';
import { CONFIG } from '../config';
import { hasCoverBetween } from './cover';
import type { Guard } from './guard';
import type { MapData } from './map';
import type { Player } from './player';
import { tickWeaponCooldowns } from './weapons';

export interface GuardShot {
  bullet: Bullet;
  loudness: number;
}

/**
 * Advance the guard's weapon cooldown; if they're alert, in range, and
 * have LOS to the player, roll hit/miss + limb and spawn a bullet toward
 * the player. Returns the bullet + loudness for the caller to push +
 * emit sound. null if the guard did not fire this tick.
 */
export function guardCombatTick(
  guard: Guard,
  player: Player,
  map: MapData,
  dt: number,
  rng: () => number = Math.random,
): GuardShot | null {
  tickWeaponCooldowns([guard.weapon], dt);
  if (guard.body.dead || player.body.dead) return null;
  if (guard.state !== 'alert') return null;
  // Shoot the guard's gun arm out and it can't fire (both arms → nothing; one
  // arm → no two-handed weapon). Same rule the player lives under.
  if (!canOperateWeapon(guard.body, guard.weapon.twoHanded ?? false)) return null;
  // A crouched guard is ducked behind cover — it never fires (it must peek/
  // stand first). This also avoids a 1-tick fov lag firing on the peek→duck
  // boundary.
  if (guard.stance === 'crouch') return null;
  if (!guardHasLosToPlayer(guard, player, map)) return null;
  const shot = attemptRangedShot(guard, player, guard.weapon);
  if (!shot) return null;
  const hit = rng() < hitChance(guard.weapon, shot.distance);
  const limb = hit ? pickLimb(rng) : null;
  // Small aim jitter so miss bullets visibly fly past instead of landing
  // dead-center on the player's tile.
  const jitter = hit ? 0 : 0.6;
  const bullet = spawnBullet({
    fromX: guard.x + 0.5,
    fromY: guard.y + 0.5,
    targetX: player.x + 0.5 + (rng() - 0.5) * jitter,
    targetY: player.y + 0.5 + (rng() - 0.5) * jitter,
    sourceId: guard.id,
    plan: { targetId: player.id, limb, damage: guard.weapon.damage },
  });
  return { bullet, loudness: shot.loudness };
}

/**
 * Manage an alert guard's stance/cover behaviour BEFORE combat + movement:
 *   - Not alert → stand (walk), reset the peek cycle.
 *   - Alert → face the player (torso aims at them; legs stay along travel).
 *     - Cover between guard and player (in range) → run the duck↔peek rhythm:
 *       crouch (hidden, can't see over cover so it won't fire) for `duckSec`,
 *       then stand for `peekSec` to take shots, repeat. The firing gate is
 *       emergent — a crouched guard's cone is blocked by cover so
 *       `guardCombatTick` naturally can't get LOS.
 *     - No cover (open LOS) → stand and fire normally.
 * Run per guard each tick, before `guardCombatTick`.
 */
export function updateGuardCover(guard: Guard, player: Player, map: MapData, dt: number): void {
  if (guard.body.dead) return;
  if (guard.state !== 'alert') {
    guard.stance = 'walk';
    guard.peeking = false;
    guard.peekTimer = 0;
    return;
  }
  // Aim the torso at the player (legs keep their travel orientation).
  guard.facingDx = Math.sign(player.x - guard.x);
  guard.facingDy = Math.sign(player.y - guard.y);

  const dist = Math.hypot(player.x - guard.x, player.y - guard.y);
  const inRange = dist <= guard.weapon.rangeTiles + 1e-6;
  const usingCover = inRange && hasCoverBetween(map, guard.x, guard.y, player.x, player.y);
  if (!usingCover) {
    // Open engagement (or repositioning): stand and shoot.
    guard.stance = 'walk';
    guard.peeking = true;
    guard.peekTimer = 0;
    return;
  }
  // Duck/peek cycle behind cover.
  guard.peekTimer += dt;
  const { duckSec, peekSec } = CONFIG.guard.cover;
  if (guard.peeking) {
    if (guard.peekTimer >= peekSec) {
      guard.peeking = false;
      guard.peekTimer = 0;
    }
  } else if (guard.peekTimer >= duckSec) {
    guard.peeking = true;
    guard.peekTimer = 0;
  }
  guard.stance = guard.peeking ? 'walk' : 'crouch';
}

/**
 * True if the guard should hold position this tick instead of pathfinding.
 * Holds when alert + in range and either has a clear shot OR is using cover
 * (there's a cover tile on the line to the player) — otherwise a ducking guard,
 * which has no LOS while crouched, would walk out of its cover. Used by
 * updateGuard to skip its moveTowards call.
 */
export function guardShouldHoldGround(guard: Guard, player: Player, map: MapData): boolean {
  if (guard.body.dead || player.body.dead) return false;
  if (guard.state !== 'alert') return false;
  const dist = Math.hypot(player.x - guard.x, player.y - guard.y);
  if (dist > guard.weapon.rangeTiles + 1e-6) return false;
  return (
    guardHasLosToPlayer(guard, player, map) ||
    hasCoverBetween(map, guard.x, guard.y, player.x, player.y)
  );
}
